/**
 * The sandboxed iframe a custom view runs in, with its loading, error and stopped states, a
 * watchdog, and the "suggest action" banner. The frame is `sandbox="allow-scripts"` only (no
 * same-origin, popups, top navigation or forms) and gets its document from `srcdoc`, built by
 * `lib/fluxViewBridge.ts` with the approved data embedded once.
 *
 * @module apps/web/src/components/flux-agent/views/FluxViewFrame
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2, MessageSquarePlus, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FRAME_MIN_HEIGHT,
  loadViewProtocol,
  viewProtocolReady,
  VIEW_RUNTIME_PATH,
  WATCHDOG_MESSAGES,
  buildViewDocument,
  clampFrameHeight,
  createMessageBudget,
  newViewToken,
  parseViewMessage,
  watchdogVerdict,
} from '@/lib/fluxViewBridge';
import { cn } from '@/lib/utils';

/** Inputs for {@link FluxViewFrame}. */
export interface FluxViewFrameProps {
  /** View title, used as the iframe's accessible name. */
  title: string;
  /** The view's TSX source. */
  source: string;
  /** Approved data keyed by source; `{}` for a view that reads nothing. */
  data: Record<string, unknown>;
  /** Fill the container's height instead of sizing to the content. Defaults to false. */
  fill?: boolean;
  /** Reveal once on first render after an explicit user open; defaults to false. Never moves focus. */
  revealOnReady?: boolean;
  /** Called when the person chooses to put a view's suggested message in the composer. */
  onSuggest?: (text: string) => void;
}

/** Where the frame is in its life. */
type Phase =
  | { /** Waiting for the runtime to render. */ kind: 'loading' }
  | { /** Runtime rendered and remains live. */ kind: 'running' }
  | {
      /** Render failed or the watchdog stopped this frame. */ kind: 'failed';
      /** Human-readable error shown beside retry. */ message: string;
      /** True when policy stopped the frame rather than a render error. */ stopped: boolean;
    };

/**
 * Render one view in its sandbox. "Try again" remounts a fresh frame with a new token; callers
 * remount this component (by `key`) when the source or data change.
 */
export function FluxViewFrame(props: FluxViewFrameProps) {
  const [attempt, setAttempt] = useState(0);
  const [ready, setReady] = useState(viewProtocolReady);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    void loadViewProtocol()
      .then(() => {
        if (live) setReady(true);
      })
      .catch(() => {
        if (live) setError('The view runtime could not be loaded. Reopen it to try again.');
      });
    return () => {
      live = false;
    };
  }, []);
  if (error) return <p role="alert">{error}</p>;
  if (!ready) return <p role="status">Preparing the view…</p>;
  return <FrameInstance key={attempt} {...props} onRetry={() => setAttempt(value => value + 1)} />;
}

/** One mounted frame with its own token, watchdog and state; never reused across attempts. */
function FrameInstance({
  title,
  source,
  data,
  fill = false,
  revealOnReady = false,
  onSuggest,
  onRetry,
}: FluxViewFrameProps & {
  /** Remount a fresh frame. */
  onRetry: () => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const revealed = useRef(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [height, setHeight] = useState(FRAME_MIN_HEIGHT);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [token] = useState(newViewToken);
  const srcDoc = useMemo(
    () =>
      buildViewDocument(`${window.location.origin}${VIEW_RUNTIME_PATH}`, {
        token,
        parentOrigin: window.location.origin,
        source,
        data,
        title,
      }),
    [token, source, data, title]
  );
  const watch = useRef({
    mountedAt: 0,
    rendered: false,
    lastHeartbeat: null as number | null,
    loads: 0,
  });

  useEffect(() => {
    watch.current = {
      mountedAt: performance.now(),
      rendered: false,
      lastHeartbeat: null,
      loads: 0,
    };
    const budget = createMessageBudget();
    let stopped = false;
    const stop = (message: string, byWatchdog: boolean) => {
      if (stopped) return;
      stopped = true;
      setPhase({ kind: 'failed', message, stopped: byWatchdog });
    };
    const onMessage = (event: MessageEvent) => {
      const message = parseViewMessage(event, frameRef.current?.contentWindow, token);
      if (!message || stopped) return;
      if (!budget.accept()) return stop(WATCHDOG_MESSAGES.flood, true);
      const now = performance.now();
      switch (message.type) {
        case 'ready':
        case 'heartbeat':
          watch.current.lastHeartbeat = now;
          break;
        case 'rendered':
          watch.current.rendered = true;
          watch.current.lastHeartbeat = now;
          setPhase({ kind: 'running' });
          break;
        case 'resize':
          setHeight(clampFrameHeight(message.height));
          break;
        case 'error':
          stop(message.message || 'This view hit an error.', false);
          break;
        case 'suggest':
          setSuggestion(message.text);
          break;
      }
    };
    window.addEventListener('message', onMessage);
    const timer = window.setInterval(() => {
      const reason = watchdogVerdict({ now: performance.now(), ...watch.current });
      if (reason) stop(WATCHDOG_MESSAGES[reason], true);
    }, 500);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
      budget.dispose();
    };
  }, [token]);

  useLayoutEffect(() => {
    if (!revealOnReady || phase.kind !== 'running' || revealed.current) return;
    revealed.current = true;
    if (frameRef.current) revealInNearestScroller(frameRef.current);
  }, [phase.kind, revealOnReady]);

  const failed = phase.kind === 'failed';
  return (
    <div className={cn('flex flex-col gap-2', fill && 'h-full')} data-testid="flux-view-frame">
      {phase.kind === 'loading' && (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Starting the view…
        </p>
      )}
      {failed ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="flux-view-error"
        >
          <AlertCircle
            className="text-destructive mt-0.5 h-3.5 w-3.5 shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="break-words">{phase.message}</p>
            <Button size="sm" variant="outline" className="mt-2 h-7 gap-1" onClick={onRetry}>
              <RotateCcw className="h-3 w-3" aria-hidden="true" /> Try again
            </Button>
          </div>
        </div>
      ) : (
        <iframe
          ref={frameRef}
          title={title}
          sandbox="allow-scripts"
          srcDoc={srcDoc}
          referrerPolicy="no-referrer"
          allow="camera 'none'; microphone 'none'; geolocation 'none'; display-capture 'none'; clipboard-read 'none'; clipboard-write 'none'; payment 'none'; usb 'none'; serial 'none'; bluetooth 'none'"
          onLoad={() => {
            watch.current.loads += 1;
          }}
          className={cn(
            'bg-background w-full rounded-md border',
            fill ? 'min-h-0 flex-1' : '',
            phase.kind === 'loading' && 'opacity-0'
          )}
          style={fill ? undefined : { height }}
          data-testid="flux-view-iframe"
        />
      )}
      {suggestion && !failed && (
        <div
          className="border-primary/30 bg-primary/5 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="flux-view-suggestion"
        >
          <MessageSquarePlus
            className="text-primary mt-0.5 h-3.5 w-3.5 shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground">This view suggests asking Flux Agent:</p>
            <p className="mt-0.5 break-words font-medium">{suggestion}</p>
            {onSuggest && (
              <Button
                size="sm"
                className="mt-2 h-7"
                onClick={() => {
                  onSuggest(suggestion);
                  setSuggestion(null);
                }}
              >
                Put it in the message box
              </Button>
            )}
          </div>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Dismiss suggestion"
            onClick={() => setSuggestion(null)}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}


/** Reveal a frame vertically in its nearest overflowing scroll container, without moving focus or outer scroll positions. */
function revealInNearestScroller(frame: HTMLIFrameElement): void {
  for (let parent = frame.parentElement; parent; parent = parent.parentElement) {
    if (!/(auto|scroll|hidden)/.test(getComputedStyle(parent).overflowY) ||
        parent.scrollHeight <= parent.clientHeight) continue;
    const bounds = parent.getBoundingClientRect();
    const top = bounds.top + parent.clientTop;
    const bottom = top + parent.clientHeight;
    const rect = frame.getBoundingClientRect();
    // A frame already spanning the viewport needs no movement; the user can scroll within it.
    if (rect.top < top && rect.bottom > bottom) return;
    // Align a clipped frame's top so its initial content has room before later size reports.
    const delta = rect.top < top || rect.bottom > bottom ? rect.top - top : 0;
    if (delta) parent.scrollBy({ top: delta, behavior: 'instant' });
    return;
  }
}
